import type { CreateJobIntakeCommand, UpdateJobCommand } from "@domain/model/Job";
import type { JobIntakeFormValues, ConstructionSiteIntakeFormValues, ClientCreateFormValues, PersonCreateFormValues, AddressEditFormValues, ClientEditFormValues, ConstructionSiteEditFormValues, JobEditFormValues, PersonEditFormValues } from "./schemas";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { CreateConstructionSiteIntakeCommand, UpdateConstructionSiteCommand } from "@/domain/model/ConstructionSite";
import type { CreateClientCommand } from "@/adapters/outbound/http/dto/ClientDto";
import type { CreatePersonCommand } from "@/adapters/outbound/http/dto/PersonDto";
import type { UpdateAddressCommand } from "@/domain/model/Address";
import type { UpdateClientCommand } from "@/domain/model/Client";
import { asAddressId, asClientId } from "@/domain/model/Ids";
import type { UpdatePersonCommand } from "@/domain/model/Person";

function emptyStringToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function normalizeGermanPhoneToE164(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;

  const phone = parsePhoneNumberFromString(trimmed, {
    defaultCountry: "DE",
    extract: false,
  });

  if (!phone || !phone.isValid()) {
    return null;
  }

  return phone.number; // E.164
}

function mapAddress(address: {
  label: string;
  street: string;
  streetNumber: string;
  zip: string;
  city: string;
  country: string;
}) {
  return {
    label: emptyStringToNull(address.label),
    street: address.street.trim(),
    streetNumber: address.streetNumber.trim(),
    zip: address.zip.trim(),
    city: address.city.trim(),
    country: address.country.trim(),
  };
}

export function toCreateJobIntakeCommand(
  values: JobIntakeFormValues,
): CreateJobIntakeCommand {
  return {
    job: {
      title: values.job.title.trim(),
      description: emptyStringToNull(values.job.description),
      notes: emptyStringToNull(values.job.notes),
      plannedStartAt: values.job.plannedStartAt ?? null,
      assignedPersonId: emptyStringToNull(values.job.assignedPersonId),
    },

    client:
      values.client.mode === "existing"
        ? {
            kind: "existing",
            clientId: values.client.clientId,
          }
        : {
            kind: "new",
            name: values.client.name.trim(),
            contactName: emptyStringToNull(values.client.contactName),
            phone: normalizeGermanPhoneToE164(values.client.phone),
            email: emptyStringToNull(values.client.email),
            address: mapAddress(values.client.address),
          },

    constructionSite:
      values.constructionSite.mode === "existing"
        ? {
            kind: "existing",
            constructionSiteId: values.constructionSite.constructionSiteId,
          }
        : values.constructionSite.mode === "client_address"
          ? {
              kind: "client_address",
              label: emptyStringToNull(values.constructionSite.label),
            }
          : {
              kind: "new_address",
              label: emptyStringToNull(values.constructionSite.label),
              address: mapAddress(values.constructionSite.address),
            },
  };
}

export function toCreateConstructionSiteIntakeCommand(
  values: ConstructionSiteIntakeFormValues,
): CreateConstructionSiteIntakeCommand {
  return {
    client:
      values.client.mode === "existing"
        ? {
            kind: "existing",
            clientId: values.client.clientId,
          }
        : {
            kind: "new",
            name: values.client.name.trim(),
            contactName: emptyStringToNull(values.client.contactName),
            phone: normalizeGermanPhoneToE164(values.client.phone),
            email: emptyStringToNull(values.client.email),
            address: mapAddress(values.client.address),
          },

    constructionSite: {
      label: emptyStringToNull(values.constructionSite.label),
      address:
        values.constructionSite.address.mode === "existing_address"
          ? {
              kind: "existing_address",
              addressId: values.constructionSite.address.addressId,
            }
          : values.constructionSite.address.mode === "client_address"
            ? {
                kind: "client_address",
              }
            : {
                kind: "new_address",
                address: mapAddress(values.constructionSite.address.address),
              },
    },
  };
}

export function toCreateClientCommand(
  values: ClientCreateFormValues,
): CreateClientCommand {
  return values.addressMode === "existing"
    ? {
        kind: "address_id",
        name: values.name.trim(),
        contactName: emptyStringToNull(values.contactName),
        phone: normalizeGermanPhoneToE164(values.phone),
        email: emptyStringToNull(values.email),
        addressId: values.addressId,
      }
    : {
        kind: "full_address",
        name: values.name.trim(),
        contactName: emptyStringToNull(values.contactName),
        phone: normalizeGermanPhoneToE164(values.phone),
        email: emptyStringToNull(values.email),
        address: mapAddress(values.address),
      };
}

export function toCreatePersonCommand(
  values: PersonCreateFormValues,
): CreatePersonCommand {
  return {
    displayName: values.displayName.trim(),
    preferredName: emptyStringToNull(values.preferredName),
    mobilePhoneE164: normalizeGermanPhoneToE164(values.mobilePhoneE164),
    email: emptyStringToNull(values.email),
    canAppearInPhoto: values.canAppearInPhoto,
  };
}

export function toUpdateAddressCommand(
  values: AddressEditFormValues,
): UpdateAddressCommand {
  return {
    label: emptyStringToNull(values.label),
    street: values.street.trim(),
    streetNumber: values.streetNumber.trim(),
    city: values.city.trim(),
    zip: values.zip.trim(),
    country: emptyStringToNull(values.country)?.toUpperCase() ?? null,
  };
}

export function toUpdateClientCommand(
  values: ClientEditFormValues,
): UpdateClientCommand {
  return {
    name: values.name.trim(),
    email: emptyStringToNull(values.email),
    phone: normalizeGermanPhoneToE164(values.phone),
    contactName: emptyStringToNull(values.contactName),
    addressId: emptyStringToNull(values.addressId),
  };
}

export function toUpdateConstructionSiteCommand(
  values: ConstructionSiteEditFormValues,
): UpdateConstructionSiteCommand {
  return {
    label: emptyStringToNull(values.label),
    addressId: emptyStringToNull(values.addressId)
      ? asAddressId(values.addressId)
      : null,
    clientId: emptyStringToNull(values.clientId)
      ? asClientId(values.clientId)
      : null,
  };
}

export function toUpdateJobCommand(
  values: JobEditFormValues,
): UpdateJobCommand {
  return {
    title: values.title.trim(),
    constructionSiteId: emptyStringToNull(values.constructionSiteId) as any,
    description: emptyStringToNull(values.description),
    notes: emptyStringToNull(values.notes),
    plannedStartAt: values.plannedStartAt ?? null,
    assignedPersonId: emptyStringToNull(values.assignedPersonId) as any,
  };
}

export function toUpdatePersonCommand(
  values: PersonEditFormValues,
): UpdatePersonCommand {
  return {
    displayName: emptyStringToNull(values.displayName),
    preferredName: emptyStringToNull(values.preferredName),
    mobilePhoneE164: normalizeGermanPhoneToE164(values.mobilePhoneE164),
    email: emptyStringToNull(values.email),
    canAppearInPhoto:
      typeof values.canAppearInPhoto === "boolean"
        ? values.canAppearInPhoto
        : null,
  };
}
